import React, { useContext, useMemo, useReducer } from "react";

import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";

const useStyles = makeStyles()(() => ({
  root: {
    paddingBottom: 16,
  },
  backDrop: {
    backdropFilter: "blur(1px)",
    backgroundColor: "transparent",
  },
  title: {
    fontWeight: 700,
    fontSize: "28px",
    color: colors.dark,
    marginBottom: 10,
    paddingInline: 32,
    textAlign: "center",
  },
  message: {
    marginBottom: 20,
    color: colors.gray,
  },
  icon: {
    height: 70,
    width: 70,
    marginBottom: 16,
  },
}));

type State = {
  open: boolean;
  title: string;
  message?: string;
};

type Action =
  | { type: "close-alert" }
  | { type: "open-alert"; payload: string; message?: string };

type AlertHelper = {
  open: (title: string, message?: string) => void;
};

const initialState: State = {
  open: false,
  title: "Sucessfully save!",
  message: "",
};

function reducer(state: State, action: Action) {
  switch (action.type) {
    case "close-alert":
      return { ...state, open: false };
    case "open-alert":
      return { open: true, title: action.payload, message: action.message };
    default:
      return state;
  }
}

const GlobalAlertContext = React.createContext<AlertHelper | null>(null);

export const GlobalAlertProvider: React.FC = ({ children }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [state, dispatch] = useReducer(reducer, initialState);

  const helpers: AlertHelper = {
    open: (title: string, message?: string) =>
      dispatch({ type: "open-alert", payload: title, message: message }),
  };

  const _handleClose = () => dispatch({ type: "close-alert" });

  const _displayDialog = useMemo(() => {
    return (
      <Dialog
        open={state.open}
        onClose={_handleClose}
        fullScreen={fullScreen}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
      >
        <DialogContent className={classes.root}>
          <DialogContentText id="alert-dialog-description">
            <Box flex={1} textAlign="right">
              <IconButton onClick={_handleClose} size="large">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center">
              <CheckCircleIcon htmlColor="#2D87BB" className={classes.icon} />
              <span className={classes.title}>{state.title}</span>
              <Typography variant="body1" className={classes.message}>
                {state.message}
              </Typography>
              <Button size="large" onClick={_handleClose}>
                Ok
              </Button>
            </Box>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  }, [state]);

  return (
    <GlobalAlertContext.Provider value={helpers}>
      {children}
      {_displayDialog}
    </GlobalAlertContext.Provider>
  );
};

const useAlert = (): AlertHelper => {
  const context = useContext(GlobalAlertContext);

  if (context == null) {
    throw new Error("No GlobalAlertContext provider found.");
  }

  return context;
};

export default useAlert;
